import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getHeroes, getNews, getAnnouncements, getTickerSettings, getNewsByCategory, getCalendarEvents } from '../services/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TodayReadingsWidget from '../components/TodayReadingsWidget';
import CatalogueSlider from '../components/CatalogueSlider';
import FeaturedAnnouncementsSection from '../components/FeaturedAnnouncementsSection';
import HeroSection from '../components/home/HeroSection';
import TickerTape from '../components/home/TickerTape';
import QuickActions from '../components/home/QuickActions';
import ChurchesSection from '../components/home/ChurchesSection';
import NewsThreeColumn from '../components/home/NewsThreeColumn';
import PriestCornerSection from '../components/home/PriestCornerSection';
import AlbumCarouselSection from '../components/home/AlbumCarouselSection';
import { AutoTranslate } from '../hooks/useAutoTranslate';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Top-level orchestrator: fetches the homepage payload once, then composes focused sub-sections.
const HomePage = () => {
  const { t } = useLanguage();
  const [heroes, setHeroes] = useState([]);
  const [, setNews] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [tickerMaxChars, setTickerMaxChars] = useState(80);
  const [weeklyCalendar, setWeeklyCalendar] = useState({
    karlskrona: [],
    karlshamn: [],
    olofstrom: [],
  });
  const [, setVaticanNews] = useState([]);
  const [, setCatholicNews] = useState([]);
  const [latestPriestBlog, setLatestPriestBlog] = useState(null);

  // Persist the parish-announcement church filter so it survives reloads
  const [selectedChurch, setSelectedChurch] = useState(
    () => localStorage.getItem('announcementChurchFilter') || 'all'
  );
  const handleChurchFilterChange = (church) => {
    setSelectedChurch(church);
    localStorage.setItem('announcementChurchFilter', church);
  };

  const loadData = useCallback(async () => {
    try {
      const [heroesData, newsData, announcementsData, tickerData, newsByCategoryData, ksk, ksh, ols] = await Promise.all([
        getHeroes(),
        getNews(5),
        getAnnouncements(null, true),
        getTickerSettings(),
        getNewsByCategory(20),
        getCalendarEvents('karlskrona', 0),
        getCalendarEvents('karlshamn', 0),
        getCalendarEvents('olofstrom', 0),
      ]);
      setHeroes(heroesData.filter((h) => h.active));
      setNews(newsData);
      setAnnouncements(announcementsData);
      setTickerMaxChars(tickerData.ticker_max_chars || 80);
      setVaticanNews(newsByCategoryData.vatican || []);
      setCatholicNews(newsByCategoryData.catholic_news || []);
      setWeeklyCalendar({
        karlskrona: ksk.masses || [],
        karlshamn: ksh.masses || [],
        olofstrom: ols.masses || [],
      });

      // Fetch latest priest blog (separate endpoint — not part of the hero payload)
      try {
        const res = await fetch(`${API_URL}/api/priest-blogs?status=published`, {
          credentials: 'include',
        });
        if (res.ok) {
          const blogs = await res.json();
          if (blogs.length > 0) setLatestPriestBlog(blogs[0]);
        }
      } catch (e) {
        console.error('Error fetching priest blog:', e);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredAnnouncements = selectedChurch === 'all'
    ? announcements
    : announcements.filter((a) => a.churches?.includes(selectedChurch));

  return (
    <div className="min-h-screen themed-page">
      <Navbar />

      <TickerTape announcements={announcements} maxChars={tickerMaxChars} />

      <HeroSection heroes={heroes} />

      <QuickActions />

      <ChurchesSection weeklyCalendar={weeklyCalendar} />

      {/* NewsThreeColumn (Församlingsmeddelanden) hidden per request — announcements still visible via Featured Announcements section */}
      {/* <NewsThreeColumn
        filteredAnnouncements={filteredAnnouncements}
        selectedChurch={selectedChurch}
        onChurchFilterChange={handleChurchFilterChange}
      /> */}

      <PriestCornerSection blog={latestPriestBlog} />

      <FeaturedAnnouncementsSection />

      {/* Catalogue Slider – Featured Items */}
      <section className="py-12" style={{ background: 'var(--theme-section-2)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">
              <AutoTranslate>Från vår katalog</AutoTranslate>
            </h2>
            <p className="text-gray-600">
              <AutoTranslate>Biblar, rosenkransar och heliga föremål</AutoTranslate>
            </p>
          </div>
          <CatalogueSlider />
        </div>
      </section>

      {/* Photo Albums — 3 latest published, self-hides when empty */}
      <AlbumCarouselSection />

      {/* Today's Reading Widget */}
      <section className="py-20" style={{ background: 'var(--theme-section-3)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <TodayReadingsWidget compact={true} />
          <Link to="/bible" className="block mt-6">
            <Button className="w-full bg-blue-600 hover:bg-blue-700">
              {t('seeAllReadings')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>

          {/* Relocated external news CTAs (previously inside Vatican / Catholic columns) */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/news?category=vatican" className="block" data-testid="view-all-vatican-news">
              <Button variant="outline" className="w-full border-blue-300 text-blue-700 hover:bg-blue-100">
                <AutoTranslate>Visa alla nyheter från Vatikanen</AutoTranslate>
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/news?category=catholic_magazines" className="block" data-testid="view-all-catholic-magazines">
              <Button variant="outline" className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-100">
                <AutoTranslate>Visa alla Katolska tidskrifter</AutoTranslate>
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
